import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Search, Zap, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLLECTIONS = [
  { id: 'prospects_bureau_agricole', label: 'Bureau Agricole' },
  { id: 'prospects_casia_fr', label: 'CASIA France' },
  { id: 'prospects_internationaux_casia', label: 'CASIA International' },
  { id: 'prospects_agri_eta', label: 'Agri ETA (Viticulteurs/Maraîchers)' },
];

export default function EnrichirCampagnesPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collection, setCollection] = useState('prospects_agri_eta');
  const [limit, setLimit] = useState(5);
  const [dryRun, setDryRun] = useState(true);
  const [rapport, setRapport] = useState(null);

  // Test domaine à la volée
  const [testNom, setTestNom] = useState('');
  const [testSite, setTestSite] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadStats = async () => {
    try {
      const r = await fetch(`${API_URL}/api/campagnes-enrichir/stats`, { headers });
      if (r.ok) setStats(await r.json());
    } catch (e) {
      toast.error('Erreur chargement stats');
    }
  };

  useEffect(() => { loadStats(); }, []);

  const lancerBatch = async () => {
    setLoading(true);
    setRapport(null);
    try {
      const r = await fetch(`${API_URL}/api/campagnes-enrichir/scraper-batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ collection, limit: Number(limit), only_generiques: true, dry_run: dryRun }),
      });
      const data = await r.json();
      if (r.ok) {
        setRapport(data);
        toast.success(`${data.enrichis}/${data.total_examines} enrichis${dryRun ? ' (dry-run)' : ''}`);
        if (!dryRun) await loadStats();
      } else {
        toast.error(data.detail || 'Erreur batch');
      }
    } catch (e) {
      toast.error('Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  const tester = async () => {
    if (!testNom && !testSite) {
      toast.error('Entre un nom ou un site');
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/campagnes-enrichir/scraper-domaine`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ nom_societe: testNom, site_url: testSite }),
      });
      const data = await r.json();
      if (r.ok) {
        setTestResult(data);
      } else {
        toast.error(data.detail || 'Erreur test');
      }
    } catch (e) {
      toast.error('Erreur serveur');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="enrichir-campagnes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Enrichissement Campagnes</h1>
          <p className="text-muted-foreground text-sm">
            Scraper 100% gratuit : site officiel + SIRENE (dirigeants) + Bing. Remplace les emails génériques (info@, contact@) par des emails nominatifs réels.
          </p>
        </div>
        <Button variant="outline" onClick={loadStats} data-testid="btn-refresh-stats">
          <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
        </Button>
      </div>

      {/* STATS GLOBALES */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {COLLECTIONS.map((c) => {
            const s = stats.collections_prospects?.[c.id] || {};
            return (
              <Card key={c.id} data-testid={`stat-card-${c.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{c.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm space-y-1">
                    <div>Total : <strong>{s.total || 0}</strong></div>
                    <div className="text-amber-600">Génériques à enrichir : <strong>{s.generiques || 0}</strong></div>
                    <div className="text-green-600">Déjà enrichis : <strong>{s.enrichis || 0}</strong></div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* FORM BATCH */}
      <Card data-testid="batch-form-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Lancer un batch d'enrichissement</CardTitle>
          <CardDescription>
            Parcourt les prospects avec email générique et tente de trouver un email nominatif. En dry-run, aucune modification en DB.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Collection</Label>
              <Select value={collection} onValueChange={setCollection}>
                <SelectTrigger data-testid="select-collection"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLLECTIONS.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre max de prospects</Label>
              <Input type="number" min="1" max="100" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="input-limit" />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex items-center gap-2">
                <Switch id="dry" checked={dryRun} onCheckedChange={setDryRun} data-testid="switch-dry-run" />
                <Label htmlFor="dry">Dry-run (test sans écriture)</Label>
              </div>
            </div>
          </div>
          <Button onClick={lancerBatch} disabled={loading} data-testid="btn-launch-batch">
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Lancer le batch
          </Button>
        </CardContent>
      </Card>

      {/* RAPPORT */}
      {rapport && (
        <Card data-testid="rapport-card">
          <CardHeader>
            <CardTitle>Rapport ({rapport.dry_run ? 'dry-run' : 'écrit en DB'})</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="secondary">Examinés : {rapport.total_examines}</Badge>
              <Badge className="bg-green-600">Enrichis : {rapport.enrichis}</Badge>
              <Badge variant="outline">Aucun nominatif : {rapport.aucun_nominatif}</Badge>
              <Badge variant="outline">Sans site : {rapport.sans_site}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prospect</TableHead>
                  <TableHead>Ancien email</TableHead>
                  <TableHead>Nouvel email</TableHead>
                  <TableHead>Nom détecté</TableHead>
                  <TableHead>Confiance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rapport.details?.map((d, i) => (
                  <TableRow key={i} data-testid={`rapport-row-${i}`}>
                    <TableCell className="font-medium">{d.nom}</TableCell>
                    <TableCell className="text-muted-foreground">{d.ancien_email || '-'}</TableCell>
                    <TableCell>
                      {d.nouveau_email ? (
                        <span className="text-green-600 font-medium">{d.nouveau_email}</span>
                      ) : (
                        <span className="text-muted-foreground italic">{d.note || d.erreur || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>{d.nom_complet || '-'}</TableCell>
                    <TableCell>
                      {d.confiance ? <Badge className="bg-blue-600">{d.confiance}</Badge> : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* TEST A LA VOLEE */}
      <Card data-testid="test-domaine-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Search className="w-5 h-5" /> Tester un domaine</CardTitle>
          <CardDescription>
            Entre un nom de société et/ou un site web pour voir tous les emails qu'on peut récupérer. Rien n'est modifié en DB.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nom société</Label>
              <Input value={testNom} onChange={(e) => setTestNom(e.target.value)} placeholder="Ex: Domaine de la Ferme Saint-Martin" data-testid="input-test-nom" />
            </div>
            <div>
              <Label>Site web</Label>
              <Input value={testSite} onChange={(e) => setTestSite(e.target.value)} placeholder="https://www.fermesaintmartin.com" data-testid="input-test-site" />
            </div>
          </div>
          <Button onClick={tester} disabled={testLoading} data-testid="btn-tester">
            {testLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Tester
          </Button>
          {testResult && (
            <div className="space-y-3 pt-3 border-t">
              <div className="text-sm">
                <strong>Domaine :</strong> {testResult.domain || '-'} · <strong>Sources :</strong> {(testResult.sources_used || []).join(', ') || 'aucune'}
              </div>
              {testResult.dirigeants?.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1">Dirigeants (SIRENE) :</div>
                  <div className="flex gap-2 flex-wrap">
                    {testResult.dirigeants.map((d, i) => (
                      <Badge key={i} variant="outline">{d.prenom} {d.nom} ({d.qualite})</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium mb-1">Candidats emails (triés par score) :</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {testResult.candidats?.slice(0, 20).map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">
                          {c.confiance >= 70 ? <CheckCircle2 className="w-3 h-3 inline mr-1 text-green-600" /> : <AlertCircle className="w-3 h-3 inline mr-1 text-amber-500" />}
                          {c.email}
                        </TableCell>
                        <TableCell className="text-xs">{c.nom_complet || '-'}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs">{c.source}</Badge></TableCell>
                        <TableCell><Badge className={c.confiance >= 70 ? 'bg-green-600' : 'bg-amber-500'}>{c.confiance}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
